import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Smartphone, 
  CheckCircle, 
  ArrowRight,
  Laptop,
  Tablet,
  Shield,
  Clock,
  CreditCard,
  Zap,
  Sun
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const DeviceFinance = () => {
  const devices = [
    {
      icon: Smartphone,
      title: "Smartphones",
      description: "Latest smartphones from top brands with flexible payment plans"
    },
    {
      icon: Laptop,
      title: "Laptops & Computers",
      description: "Business laptops and desktops for productivity"
    },
    {
      icon: Tablet,
      title: "Tablets",
      description: "Tablets for business and personal use"
    },
    {
      icon: Sun,
      title: "Solar Equipment",
      description: "Solar panels, inverters, and solar power systems for home and business"
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Quick Approval",
      description: "Get approved within 24 hours with minimal documentation"
    },
    {
      icon: CreditCard,
      title: "Flexible Payments",
      description: "Spread your payments over 3-24 months"
    },
    {
      icon: Shield,
      title: "Device Protection",
      description: "Optional insurance coverage for your devices"
    },
    {
      icon: Clock,
      title: "Easy Process",
      description: "Simple online application with fast delivery"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-8 object-contain" />
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Business Loans
              </Link>
              <Link to="/wholesale-lending" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Microfinance Wholesale Loans
              </Link>
              <Link to="/individual-loans" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Individual Loans
              </Link>
              <Link to="/device-finance" className="text-[#0A2540] font-medium">
                Device Finance
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A2540] hover:bg-gray-100">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
            <Smartphone className="h-5 w-5 text-amber-400" />
            <span className="text-white text-sm font-medium">Device Finance</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get the Devices You Need Today
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Finance smartphones, laptops, tablets, and solar equipment with affordable monthly payments
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/device-finance/apply">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8">
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/knowledge">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">$100 - $5K</p>
              <p className="text-gray-600 text-sm mt-1">Finance Amount</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">5% - 8%</p>
              <p className="text-gray-600 text-sm mt-1">Monthly Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">3 - 24</p>
              <p className="text-gray-600 text-sm mt-1">Months Term</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">24hrs</p>
              <p className="text-gray-600 text-sm mt-1">Approval Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Devices */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">What Can You Finance?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get the technology you need with our flexible device financing options
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {devices.map((device, index) => (
              <Card key={index} className="bg-white hover:shadow-lg transition-shadow text-center">
                <CardHeader>
                  <div className="h-16 w-16 rounded-full bg-[#0A2540]/10 flex items-center justify-center mx-auto mb-4">
                    <device.icon className="h-8 w-8 text-[#0A2540]" />
                  </div>
                  <CardTitle className="text-xl">{device.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{device.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Why Finance With Us?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-[#0A2540] mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">How It Works</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Choose Device", desc: "Select the device you want to finance" },
              { step: "2", title: "Apply Online", desc: "Complete our simple application form" },
              { step: "3", title: "Get Approved", desc: "Receive approval within 24 hours" },
              { step: "4", title: "Get Device", desc: "Pick up or get your device delivered" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="h-12 w-12 rounded-full bg-[#0A2540] text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-[#0A2540] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/device-finance/apply">
              <Button size="lg" className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Start Your Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">Tzur Finances is a registered microfinance institution</p>
          <p className="text-sm mt-2">© 2024 Tzur Finances. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DeviceFinance;
